import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomGenericTableComponent, HeaderType, UiKitModule } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  ClientEnvironment,
  CommonUtilityLib,
  ENVIRONMENT,
  OptionsLabelPipe,
  Preferences,
  TableColumn,
  UserPreferencesService,
  ValueListsService,
  ViewType,
} from '@thehubz/client/core';
import { Asset } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { Subject, Subscription, from, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getBrandMarkeListSEAT, getBrandModelListSEAT } from '../../customer/customer-metadata-config';
import { AssetCommonService } from '../../services/asset-common.service';
import { AssetViewTypeService } from '../../services/asset-view-type.service';
import { AssetProgressBarComponent } from '../asset-progress-bar/asset-progress-bar.component';
import { AssetActionMenuComponent } from './asset-action-menu/asset-action-menu.component';

@Component({
  selector: 'thehubz-asset-listview',
  templateUrl: './asset-listview.component.html',
  styleUrls: ['./asset-listview.component.scss'],
  standalone: true,
  imports: [CommonModule, UiKitModule, MatIconModule, MatMenuModule, AssetActionMenuComponent, AssetProgressBarComponent, TourMatMenuModule],
})
export class AssetListViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() assetData: AssetUIModel[] = [];
  @Input() filterObject: AnyType = {};
  @Input() assetCount!: number;
  @Input() configObject!: AnyType;
  @Input() dashboardFlag? = false;
  @Input() projectFlag? = false;
  @Input() collectionFlag? = false;
  @Input() AssetType = Asset.AssetTypeEnum;
  @Input() size = 4;
  @Input() tableColumnData: AnyType;
  @Input() collectionOwner = false;
  @Input() checkedAll? = false;
  @Input() loading? = false;
  @Input() offset!: number;
  @Input() tableType = 'asset';
  @Input() gridSettingsKeySuffix = '';
  @Input() assetUploadStepperFlag?: boolean;
  @Input() headerType: HeaderType = 'assetList';
  @Input() selectedTabKey = 'assetListView';

  @Output() deleteAsset = new EventEmitter<{ asset: AssetUIModel; id: string; planDeletionFlag: boolean }>();
  @Output() downloadAsset = new EventEmitter<{ asset: AssetUIModel; name: string }>();
  @Output() addToCollection = new EventEmitter<AssetUIModel>();
  @Output() exportAsset = new EventEmitter();
  @Output() changeView = new EventEmitter<ViewType>();
  @Output() sortBy = new EventEmitter();
  @Output() clickSortFilter = new EventEmitter<AnyType>();
  @Output() tableScrolled = new EventEmitter<{ offset: number; direction?: string; anchorIndex?: number }>();
  @Output() updateSelectedData = new EventEmitter<AssetUIModel[]>();
  @Output() addRemoveFromBasket = new EventEmitter<AssetUIModel>();
  @Output() updateCollectionAssets = new EventEmitter<{ asset: AssetUIModel; checked: boolean }>();
  @Output() tableData = new EventEmitter<AnyType>();
  @Output() uploadClick = new EventEmitter<AnyType>();

  @Output() editAssets = new EventEmitter<AnyType>();

  @ViewChild(CustomGenericTableComponent) customGenericTable!: CustomGenericTableComponent;

  sortByNew = true;

  dataViewFlag = false;
  columns!: TableColumn[];
  additionalColumns!: TableColumn[];
  groupedViewFlag = false;
  selectedViewType!: ViewType;

  selectedTableColumnSubscription?: Subscription;
  selectedData: AssetUIModel[] = [];
  itemSize = 100;
  httpCallInProgress = false;
  scrollFlag = true;
  tableOffset = 0;
  CustomClients = CustomClients;
  brandingKey!: CustomClients;

  lastVisibleAssetIndex = 0;

  isGuidedTourEnabled?: boolean;

  private destroy$ = new Subject<void>();

  private scrollerSubscription?: Subscription;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private userPreferences: UserPreferencesService,
    private assetCommonService: AssetCommonService,
    private router: Router,
    private optionsLabel: OptionsLabelPipe,
    private activatedRoute: ActivatedRoute,
    private valueListsService: ValueListsService,
    private commonUtilityLib: CommonUtilityLib,
    protected assetViewTypeService: AssetViewTypeService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
    this.selectedTableColumnSubscription = this.userPreferences.selectedTableColumn.subscribe((data) => {
      if (data && data.changeType === 'assetList') {
        this.columns = data.defaultColumn;
        this.additionalColumns = data.additionalColumn;
        const assetColumns: Preferences = { defaultColumns: this.columns, additionalColumns: this.additionalColumns };
        if (data.resetViewFlag) {
          this.userPreferences.clearUserPreferences(this.selectedTabKey, 'asset', 'resetColumns');
        } else {
          this.userPreferences.setUserPreferences(this.selectedTabKey, 'asset', assetColumns);
        }
        this.tableData.emit({ columns: this.columns, additionalColumns: this.additionalColumns });
      }
    });

    this.isGuidedTourEnabled = env.isGuidedTourEnabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assetData']?.currentValue?.length) {
      this.updateACB();
    }
  }

  ngOnInit() {
    this.fetchColumns();
    this.selectedViewType = this.assetViewTypeService.getViewType(this.gridSettingsKeySuffix);
  }

  updateACB() {
    for (const asset of this.assetData) {
      if (asset.domains?.length) {
        const firstAssetDomain = asset.domains[0];
        this.valueListsService.lookACB(firstAssetDomain.agency, firstAssetDomain.client, firstAssetDomain.brand).subscribe((data: AnyType) => {
          asset.agency = data.agency ?? asset.agency;
          asset.client = data.client ?? asset.client;
          asset.brand = data.brand ?? asset.brand;
          if (firstAssetDomain?.brandMarke) asset['brandMarke'] = this.optionsLabel.transform(firstAssetDomain.brandMarke, getBrandMarkeListSEAT());
          if (firstAssetDomain?.brandModel)
            asset['brandModel'] = this.optionsLabel.transform(
              firstAssetDomain?.brandModel,
              getBrandModelListSEAT(firstAssetDomain.brandMarke?.toLowerCase() || ''),
            );
        });
      }
    }
  }

  scrollToIndex(index: number, behavior: ScrollBehavior = 'instant') {
    this.customGenericTable.scrollToIndex(index, behavior);
  }

  ngAfterViewInit(): void {
    const newInterval = interval(100).subscribe(() => {
      if (!this.customGenericTable) return;
      if (this.customGenericTable?.scroller) {
        newInterval.unsubscribe();
        this.scrollerSubscription = this.customGenericTable.scroller.elementScrolled().subscribe(() => {
          this.changeProgress();
        });
      }
    });
    const assetListInterval = interval(100).subscribe(() => {
      const elements = document.querySelectorAll('.asset-list-image');
      if (!elements.length) return;
      this.changeProgress();
      assetListInterval.unsubscribe();
    });
  }

  clickLoadMore(event: { offset: number; anchorIndex?: number }) {
    if (event.offset > -1 && event.offset < this.offset) {
      const skip = this.filterObject['limit'] * event.offset;
      this.filterObject['skip'] = this.assetCount > skip ? skip : 0;
      this.tableScrolled.emit({ offset: event.offset, direction: 'top', anchorIndex: event.anchorIndex });
    } else {
      const skip = this.filterObject['limit'] * event.offset;
      this.filterObject['skip'] = this.assetCount > skip ? skip : 0;
      if (this.filterObject['skip'] > 0) {
        this.tableScrolled.emit({ offset: event.offset });
      }
    }
  }

  changeProgress() {
    this.lastVisibleAssetIndex = this.getAssetIndexFromElement(this.findLastVisibleAsset());
  }

  private findLastVisibleAsset() {
    const elements = document.querySelectorAll('.asset-list-image');
    let lastVisibleElement = null;
    const scrollerBoxBottom = this.customGenericTable.scroller.elementRef.nativeElement.getBoundingClientRect().bottom;
    elements.forEach((element: AnyType) => {
      const elBoxTop = element.getBoundingClientRect().top;
      if (scrollerBoxBottom > elBoxTop) {
        lastVisibleElement = element;
      }
    });
    return lastVisibleElement;
  }

  private getAssetIndexFromElement(element: AnyType) {
    const elementId = element?.getAttribute('asset-id');
    const assetIndex = this.assetData.findIndex((item) => item._id === elementId);
    return assetIndex;
  }

  /**
   * Fetch tab specific columns based on the tab key
   */
  private fetchColumns() {
    from(this.userPreferences.tabTableConfig(this.selectedTabKey, this.tableType))
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        const tabTableConfig = config;
        this.columns = tabTableConfig && tabTableConfig.defaultColumns ? tabTableConfig.defaultColumns || [] : this.tableColumnData.TABLE_DEFAULT_COLUMNS;
        this.additionalColumns = tabTableConfig && tabTableConfig.additionalColumns ? tabTableConfig.additionalColumns || [] : this.tableColumnData.ADD_COLUMNS;
        // Arval changes to the asset list view
        // hide agency client brand columns
        if (this.brandingKey === CustomClients.Arval) {
          this.columns = this.commonUtilityLib.hideColumn(['client', 'brand'], this.columns);
          this.additionalColumns = this.commonUtilityLib.hideColumn(['agency'], this.additionalColumns);
        }
        this.tableData.emit({ columns: this.columns, additionalColumns: this.additionalColumns });
      });
  }

  openModal(asset: AssetUIModel, assetId: number, rowIndex: number) {
    if (asset.inProgress) return;
    // add params on opening dialog
    if (this.configObject?.callFrom !== 'createProject') this.assetIDToParam(asset._id);
    if (this.projectFlag) {
      // To Do
      const currentIndex = assetId * this.size + rowIndex;
      this.updateSelectedObject(this.assetData[currentIndex]);
    } else {
      this.assetCommonService.updateAssetList(this.assetData);
      sessionStorage.setItem('assetsGalleryIdsList', JSON.stringify(this.assetData.map((asset: { _id: AnyType }) => asset._id)));
      if (!this.configObject || this.configObject?.callFrom === 'collection') {
        const assetPosition = 'row-' + assetId;
        if (this.filterObject.skip > 0 || this.filterObject.limit > 48) {
          // Round up to the nearest 48
          const assetFutureLimit = Math.ceil(rowIndex / 48) * 48;
          if (assetFutureLimit) sessionStorage.setItem('assetFutureLimit', assetFutureLimit.toString());
        }
        sessionStorage.setItem('assetListViewScrollPosition', assetPosition);
      }
      this.assetViewDialog(asset);
    }
  }

  updateSelectedObject(data: AssetUIModel) {
    this.selectedData.push(data);
    this.updateSelectedData.emit(this.selectedData);
  }

  assetViewDialog(asset: AssetUIModel) {
    // configuration coming from share-assets
    // redirect to variant page
    if (this.configObject && this.configObject?.callFrom === 'shareAssets') {
      this.router.navigate(['sharing/variant', asset._id]);
    } else {
      if (this.brandingKey === CustomClients.Vorwerk && asset.status === Asset.StatusEnum.Draft) {
        const event = { assets: [asset] };
        this.uploadClick.emit(event);
        return;
      } else {
        this.router.navigate(['/assets/detail', asset._id]);
      }
    }
  }

  editDraftAsset(event: AnyType) {
    this.uploadClick.emit(event);
  }

  // method to add asset id to query param
  assetIDToParam(assetID?: string) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        assetId: assetID ? assetID : null,
      },
      // preserve the existing query params in the route
      queryParamsHandling: 'merge',
      // do not trigger navigation
      skipLocationChange: false,
    });
  }

  getAsset(id: string) {
    const asset = this.assetData.filter((asset: AssetUIModel) => asset._id === id)[0];
    return asset;
  }

  onDownloadAsset(id: string) {
    const asset = this.getAsset(id);
    const name = asset.name;
    this.downloadAsset.emit({ asset, name });
  }

  onDeleteAsset(id: string, planDeletionFlag: boolean) {
    const asset = this.getAsset(id);
    this.deleteAsset.emit({ asset, id, planDeletionFlag });
  }

  onClickBasket(id: string) {
    const asset = this.getAsset(id);
    this.addRemoveFromBasket.emit(asset);
  }
  onSelectAsset(event: AnyType) {
    const asset = this.getAsset(event.data?._id ?? event._id);
    if (this.collectionFlag) {
      this.updateCollectionAssets.emit({ asset: event.data, checked: event.checked });
    } else {
      this.addRemoveFromBasket.emit(asset);
    }
  }

  onAddToCollection(id: string) {
    const asset = this.getAsset(id);
    this.addToCollection.emit(asset);
  }

  onExportAsset(id: string) {
    this.exportAsset.emit();
  }

  menuOpened(id: string) {
    const row = document.getElementById(`row-${id}`);
    if (row) {
      row.classList.add('highlight');
    }
  }

  menuClosed(id: string) {
    const row = document.getElementById(`row-${id}`);
    if (row) {
      row.classList.remove('highlight');
    }
  }

  onClickSortOption() {
    this.sortByNew = !this.sortByNew;
    this.sortBy.emit();
  }

  onClickSortFilter(event: Event) {
    this.clickSortFilter.emit(event);
  }

  ngOnDestroy(): void {
    this.selectedTableColumnSubscription && this.selectedTableColumnSubscription.unsubscribe();
    this.scrollerSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
